import React, { useCallback, useEffect, useRef, useState } from 'react';

interface IPoint {
	x: number;
	y: number;
}

const DRAG_THRESHOLD_PX = 4;

const readStored = (key: string): IPoint | null => {
	try {
		const raw = localStorage.getItem(key);
		const p = raw ? JSON.parse(raw) : null;
		return p && typeof p.x === 'number' && typeof p.y === 'number' ? p : null;
	} catch {
		return null;
	}
};

// Drag a fixed-position element with the pointer; the spot is remembered per browser. A press that
// moves less than the threshold is still a click (consumeDragClick tells the click handler apart).
// Buttons inside the element don't start a drag.
export function useDragPosition(storageKey: string) {
	const ref = useRef<HTMLDivElement | null>(null);
	const [pos, setPos] = useState<IPoint | null>(() => readStored(storageKey));
	const [dragging, setDragging] = useState(false);
	const dragRef = useRef<{ startX: number; startY: number; originX: number; originY: number; moved: boolean } | null>(null);
	const clickSuppressedRef = useRef(false);

	const clamp = useCallback((p: IPoint): IPoint => {
		const el = ref.current;
		const w = el?.offsetWidth || 0;
		const h = el?.offsetHeight || 0;
		return {
			x: Math.min(Math.max(0, p.x), Math.max(0, window.innerWidth - w)),
			y: Math.min(Math.max(0, p.y), Math.max(0, window.innerHeight - h)),
		};
	}, []);

	useEffect(() => {
		if (!pos) return;
		try {
			localStorage.setItem(storageKey, JSON.stringify(pos));
		} catch { /* private mode etc. */ }
	}, [pos, storageKey]);

	// keep it on screen when the window shrinks
	useEffect(() => {
		const onResize = () => setPos(p => p && clamp(p));
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, [clamp]);

	const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
		if (e.button !== 0) return;
		if ((e.target as HTMLElement).closest('button')) return;
		const el = ref.current;
		if (!el) return;
		const r = el.getBoundingClientRect();
		dragRef.current = { startX: e.clientX, startY: e.clientY, originX: r.left, originY: r.top, moved: false };
		el.setPointerCapture(e.pointerId);
	};

	const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
		const d = dragRef.current;
		if (!d) return;
		const dx = e.clientX - d.startX;
		const dy = e.clientY - d.startY;
		if (!d.moved) {
			if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
			d.moved = true;
			setDragging(true);
		}
		setPos(clamp({ x: d.originX + dx, y: d.originY + dy }));
	};

	const onPointerUp = () => {
		const d = dragRef.current;
		dragRef.current = null;
		if (d?.moved) {
			clickSuppressedRef.current = true;
			setDragging(false);
		}
	};

	// true once, right after a drag: the click that ends it must not count as a click
	const consumeDragClick = () => {
		const suppressed = clickSuppressedRef.current;
		clickSuppressedRef.current = false;
		return suppressed;
	};

	return {
		ref,
		dragging,
		consumeDragClick,
		// overrides the default corner once the user has moved it
		positionSx: pos ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : {},
		handlers: {
			onPointerDown,
			onPointerMove,
			onPointerUp,
			onPointerCancel: onPointerUp,
		},
	};
}

export const PREVIEW_POSITION_KEY = 'callPreviewPosition';
